import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { SourcingOpenings, SourcingService } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import TrashIcon from '@/assets/icons/trash.svg';
import { useToaster } from '@/components/base/toaster';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { ActionToast } from '@/components/ui/action-toast';
import { useAuthContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

interface RowsSelectProps {
  table: Table<SourcingOpenings>;
  refetchOpenings: () => Promise<void>;
}

export function RowsSelect({ table, refetchOpenings }: RowsSelectProps) {
  const queryClient = useQueryClient();
  const { rowSelection } = table.getState();
  const { notifySuccess, notifyError } = useToaster();
  const intl = useIntl();
  const { canPostToJobBoards } = useAuthContext();
  const { setOpenChannelsDialog, setSelectedOpeningIds } =
    useJobPostingContext();

  const selectedIds = useMemo(
    () => (_isEmpty(rowSelection) ? [] : Object.keys(rowSelection)),
    [rowSelection],
  );

  const { mutate: updateSelection } = useMutation({
    mutationFn: () =>
      SourcingService.postInternalApiSourcingOpeningsMarkForSourcing({
        unselected_opening_ids: selectedIds,
      }),
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Selected openings updated successfully!',
        }),
      });
      table.resetRowSelection();
      await refetchOpenings();
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.dashboard.recommendations.sourcing],
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update the selected openings!',
        }),
      });
    },
  });

  const onSelectedOpeningsSubmit = useCallback(() => {
    updateSelection();
  }, [updateSelection]);

  const handlePostJobClick = useCallback(() => {
    setSelectedOpeningIds(selectedIds);
    setOpenChannelsDialog(true);
  }, [selectedIds, setSelectedOpeningIds, setOpenChannelsDialog]);

  const actionButtons = useMemo(() => {
    const buttons = [];

    // Only show post job button if user has permission
    if (canPostToJobBoards) {
      buttons.push({
        label: intl.formatMessage({
          defaultMessage: 'Post job',
        }),
        onClick: handlePostJobClick,
        icon: <PlaneSendIcon className="text-fx-neutral-400" />,
      });
    }

    buttons.push({
      label: intl.formatMessage({
        defaultMessage: 'Remove selection from dashboard',
      }),
      onClick: onSelectedOpeningsSubmit,
      icon: <img src={TrashIcon} alt="Chart bar" />,
    });

    return buttons;
  }, [canPostToJobBoards, handlePostJobClick, intl, onSelectedOpeningsSubmit]);

  if (_isEmpty(selectedIds)) return <></>;

  return <ActionToast actionButtons={actionButtons} />;
}
